import { AfterViewInit, Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { BusinessSearchCriteria, EnumeratedTypesRepositoryService, JurisdictionInfo, Order, OrderManagerService, ValidationProblem, ValidationService, eBusinessSearchCriteriaType, SearchParameters, instantValidationThrottle } from 'reg-hub-common';
import { eCriteriaVariation } from '../../services/stepper/steps/search-stepper';
import { SteppedComponent } from 'projects/reg-hub-client/src/interfaces/stepped-component';
import { StepValidationState } from 'projects/reg-hub-client/src/interfaces/step';
import { BehaviorSubject, takeUntil } from 'rxjs';
import { EnvironmentUrlService } from '../../services/environment-url/environment-url.service';

@Component({
  selector: 'app-us-business-search-criteria',
  standalone: false,
  templateUrl: './us-business-search-criteria.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './us-business-search-criteria.component.css'
})
export class UsBusinessSearchCriteriaComponent extends SteppedComponent implements AfterViewInit {
  @Input() criteriaVariations: eCriteriaVariation[] = []
  @Input() isFormEnabled: boolean = true;
  protected jurisdictions: JurisdictionInfo[] = [];

  protected searchCriteriaDetails!: FormGroup;

  protected nameSearch = eBusinessSearchCriteriaType.Name;
  protected numberSearch = eBusinessSearchCriteriaType.Number;
  
  protected displayNameSearchFields: boolean = false;
  protected displayNumberSearchFields: boolean = false;

  nameError$ = new BehaviorSubject<string | null | undefined>(null);
  numberError$ = new BehaviorSubject<string | null | undefined>(null);
  jurisdictionsError$ = new BehaviorSubject<string | null | undefined>(null);

  private orderToValidate: Order | null = null;

  constructor(
    private formBuilder: FormBuilder,
    private environmentUrl: EnvironmentUrlService,
    private enumeratedTypesRepo: EnumeratedTypesRepositoryService,
    orderManager: OrderManagerService,
    validationService: ValidationService) {
    super(orderManager, validationService);
  }

  protected override init(order: Order): void {
    super.init(order);

    const criteria = this.order.businessSearchCriteria;
    if (criteria?.businessSearchCriteriaTypeID.toString() == "Name") {
      criteria!.businessSearchCriteriaTypeID = this.nameSearch;
    }

    if (criteria?.businessSearchCriteriaTypeID.toString() == "Number") {
      criteria!.businessSearchCriteriaTypeID = this.numberSearch;
    }

    this.searchCriteriaDetails = this.formBuilder.group({
      businessSearchCriteriaTypeID: [criteria?.businessSearchCriteriaTypeID],
      name: [criteria?.name],
      number: [criteria?.number],
      jurisdictions: [[]]
    })

    if (!this.isFormEnabled) {
      this.searchCriteriaDetails.disable();
    }

    this.searchCriteriaDetails.get('businessSearchCriteriaTypeID')!.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(val => {
        this.displayNameSearchFields = (val === this.nameSearch || val === "Name");
        this.displayNumberSearchFields = (val === this.numberSearch || val === "Number");
      });
    this.searchCriteriaDetails.valueChanges
      .pipe(
        takeUntil(this.onDestroy$),
        instantValidationThrottle())
      .subscribe(() => this.validate(this.getOrderToValidate()));
  }

  ngAfterViewInit(): void {
    this.displayNameSearchFields = (this.searchCriteriaDetails.get('businessSearchCriteriaTypeID')!.value === this.nameSearch || this.searchCriteriaDetails.get('businessSearchCriteriaTypeID')!.value === "Name");
    this.displayNumberSearchFields = (this.searchCriteriaDetails.get('businessSearchCriteriaTypeID')!.value === this.numberSearch || this.searchCriteriaDetails.get('businessSearchCriteriaTypeID')!.value === "Number");
  }

  public override getValidationState(errors: ValidationProblem[] | undefined, order: Order): StepValidationState {
    return (errors?.length ?? 0) === 0 ? StepValidationState.ValidationSuccess : StepValidationState.ValidationError;
  }

  public override onSaving(): void {
    const criteria = this.order.businessSearchCriteria;
    let parameters = this.order.searchParameters;

    if(!criteria) {
      return;
    }

    if(!parameters) {
      parameters = { } as SearchParameters;
    }

    this.updateBusinessSearchCriteria(criteria);
    this.order.businessSearchCriteria = criteria;

    this.orderManager.updateOrder(this.order);
  }

  override pushErrors(errors: ValidationProblem[] | undefined): void {
    if(!errors) {
      return;
    }

    this.nameError$.next(errors.filter(error => error.path.includes('/name'))?.at(0)?.userFriendlyMessage);
    this.numberError$.next(errors.filter(error => error.path.includes('/number'))?.at(0)?.userFriendlyMessage);
    this.jurisdictionsError$.next(errors.filter(error => error.path.includes('/jurisdictions'))?.at(0)?.userFriendlyMessage);
  }

  private getOrderToValidate(): Order {
    if(!this.orderToValidate) {
      this.orderToValidate = this.copyOrder(this.order);
    }

    const criteria = this.orderToValidate.businessSearchCriteria;

    if(!criteria) {
      return this.orderToValidate!;
    }

    this.updateBusinessSearchCriteria(criteria);
    this.orderToValidate!.businessSearchCriteria = criteria;

    return this.orderToValidate!;
  }

  private updateBusinessSearchCriteria(criteria: BusinessSearchCriteria): void {
    criteria.businessSearchCriteriaTypeID = this.searchCriteriaDetails.get('businessSearchCriteriaTypeID')!.value;
    criteria.name = this.searchCriteriaDetails.get('name')!.value;
    criteria.number = null;
  }
}